import type { Object3D } from 'three';
import { isFeatureFace, isFeatureLine, type FeatureLine, type FeatureResultMesh } from '../../../../mesh/feature';
import {
	isSketchArcObject,
	isSketchCircleObject,
	isSketchCollectionObject,
	isSketchEllipseObject,
	isSketchLineObject,
	isSketchParabolaObject,
	isSketchPointObject,
	isSketchSlotObject,
	isSketchSplineObject,
	isSketchStyleSplineObject,
} from '../../../../mesh/guards';
import { isSketchContour } from '../../../../mesh/meshProxy/SketchContour';
import type { SketchCollectionObject } from '../../../../mesh/sketch/SketchCollectionObject';
import type { SketchSlotObject } from '../../../../mesh/sketch/SketchSlotObject';
import { findAncestorSketchGroupPersistentId } from '../../../../manager/SketchEditingInteractionGuard';
import { resolveOwningFeatureResultMesh } from '../../../../utils/feature/resolveFeatureSceneMesh';
import { resolveSourceDisplayLabel } from '../../model/EdgeLabelUtils';
import {
	resolveExternalGeometrySourceDescriptorKey,
	resolveTopologySourceOwnerIdentityKey,
	type ExternalGeometrySourceDescriptor,
	type TopologyEdgeMatchHints,
	type TopologyEdgeSourceDescriptor,
	type TopologySourceOwnerDescriptor,
} from '../../model/ExternalGeometrySourceDescriptor';
import type {
	SketchEntityReferenceSelectionCandidate,
	SketchEntityReferenceSelectionCandidateSource,
} from './SketchEntityReferenceSelectionAdapter';
import { SketchCurveChainExpansionService } from './SketchCurveChainExpansionService';
import type {
	SketchEntityReferenceSelectionCandidateResolverOptions,
	SketchEntityReferenceResolvedCandidate,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceSelectionCandidateResolver';

export type {
	SketchEntityReferenceSelectionCandidateResolverOptions,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceSelectionCandidateResolver';

/**
 * 将向量端点转为固定长度元组。
 *
 * @param point 世界坐标点。
 * @returns 固定长度坐标元组。
 */
function toWorldCoordinateTuple(
	point: { x: number; y: number; z: number },
): [number, number, number] {
	return [point.x, point.y, point.z];
}

/**
 * 读取当前 topology 边代理的可信曲线类型。
 *
 * @param featureLine 当前运行时边代理。
 * @param edgeSnapshot 当前 owner 中命中的 topology 边快照。
 * @returns 能确认的曲线类型；缺失时返回 `other`，避免把未知采样曲线误收窄为直线。
 */
function resolveFeatureLineCurveKind(
	featureLine: FeatureLine,
	edgeSnapshot: { curveKind?: TopologyCurveKind } | null,
): TopologyCurveKind {
	if (typeof edgeSnapshot?.curveKind === 'string') {
		return edgeSnapshot.curveKind;
	}

	const runtimeCurveKind = Reflect.get(featureLine, 'curveKind');
	return typeof runtimeCurveKind === 'string'
		? runtimeCurveKind as TopologyCurveKind
		: 'other';
}

/**
 * 判断脱离 topology snapshot 的边代理是否仍携带 sampled 曲线几何。
 *
 * @param featureLine 当前运行时边代理。
 * @returns 顶点数超过 2 时返回 true，表示不能安全按单一直线端点解释。
 */
function hasFeatureLineSampledCurveGeometry(featureLine: FeatureLine): boolean {
	const positionAttribute = featureLine.geometry.getAttribute('position');
	return typeof positionAttribute?.count === 'number' && positionAttribute.count > 2;
}

/**
 * 生成复合选择意图的来源集合稳定 key。
 *
 * 说明：
 * 1. 选择链从不同成员边触发时，命中的 `Object3D.uuid` 会不同；
 * 2. 链展开顺序还可能随成员边绘制方向出现正反差异；
 * 3. SolidWorks 的选择列表表达的是一条链本身，因此同一条链必须落到同一个选择意图 ID；
 * 4. 这里复用并排序持久化 descriptor key，让 UIBridge 的冲突删除和高亮回收都基于真实来源集合收口。
 *
 * @param sources 选择意图展开后的来源集合。
 * @returns 来源集合稳定 key。
 */
function buildSelectionIntentSourceListKey(
	sources: readonly SketchEntityReferenceSelectionCandidateSource[],
): string {
	return sources
		.map((source) => resolveExternalGeometrySourceDescriptorKey(source.descriptor))
		.sort()
		.join('|');
}

/**
 * 草图实体引用选择候选解析器。
 * 说明：
 * 1. 宿主层只负责把当前命中的 `Object3D` 交给 cad-core；
 * 2. 解析器统一判断“是否为外部草图曲线源”以及“是否为可转换的 topology 边/面来源”；
 * 3. 这样可以避免 web-client 自己重复拼 descriptor、显示名与目标草图排除逻辑。
 */
export class SketchEntityReferenceSelectionCandidateResolver {
	private readonly chainExpansionService = new SketchCurveChainExpansionService();

	/**
	 * 解析单个命中对象。
	 *
	 * @param input 解析输入。
	 * @returns 命中时返回候选；不支持时返回 null。
	 */
	public resolve(input: {
		targetObject: Object3D | null;
		targetSketchId: string;
		options?: SketchEntityReferenceSelectionCandidateResolverOptions;
	}): SketchEntityReferenceSelectionCandidate | null {
		if (!input.targetObject) {
			return null;
		}

		const sketchTargetObject = this.resolveSketchEntityTargetObject(input.targetObject);
		const sketchEntityCandidate = this.resolveSketchEntityCandidate(
			sketchTargetObject,
			input.targetSketchId,
			input.options,
		);
		if (sketchEntityCandidate) {
			return sketchEntityCandidate.candidate;
		}

		const featureEdgeCandidate = this.resolveFeatureEdgeCandidate(input.targetObject);
		if (featureEdgeCandidate) {
			return featureEdgeCandidate.candidate;
		}

		return null;
	}

	/**
	 * 解析参与草图实体引用候选判断的实体对象。
	 *
	 * 说明：
	 * 1. 抛物线的焦点、顶点和起终点都紧贴曲线，屏幕拾取时控制点会先于曲线本体命中；
	 * 2. Convert Entities 的语义仍然是选择草图曲线，不允许把点本身作为引用来源；
	 * 3. 闭合轮廓面的选择事件可能只携带 `SketchContour`，需要先回溯到真实来源集合；
	 * 4. 因此仅当控制点能唯一回溯到一条抛物线时，把它视作该抛物线的拾取代理继续解析。
	 *
	 * @param targetObject 当前命中对象。
	 * @returns 应进入草图实体候选判断的对象。
	 */
	private resolveSketchEntityTargetObject(targetObject: Object3D): Object3D {
		if (isSketchContour(targetObject)) {
			return targetObject.source;
		}

		const parabolaOwner = this.resolveParabolaOwnerFromControlPoint(targetObject);
		return parabolaOwner ?? targetObject;
	}

	/**
	 * 从抛物线控制点解析所属抛物线。
	 *
	 * @param targetObject 当前命中对象。
	 * @returns 唯一所属抛物线；无法确认唯一归属时返回 null。
	 */
	private resolveParabolaOwnerFromControlPoint(targetObject: Object3D): Object3D | null {
		if (!isSketchPointObject(targetObject)) {
			return null;
		}

		const connectedEdges = targetObject.getConnectedEdgesSnapshot();
		const connectedParabolas = connectedEdges.filter(isSketchParabolaObject);
		if (connectedParabolas.length !== 1) {
			return null;
		}

		/**
		 * 真实项目恢复或复制链路中，控制点角色标记可能缺失；
		 * Convert Entities 只需要把“唯一连接到抛物线的点”视作曲线拾取代理，
		 * 共享端点可能同时连接直线/圆弧，但只要抛物线归属唯一，就仍可稳定回溯。
		 */
		return connectedParabolas[0] ?? null;
	}

	/**
	 * 解析外部草图实体候选。
	 *
	 * @param targetObject 当前命中对象。
	 * @param targetSketchId 当前目标草图 id。
	 * @returns 命中时返回候选；否则返回 null。
	 */
	private resolveSketchEntityCandidate(
		targetObject: Object3D,
		targetSketchId: string,
		options: SketchEntityReferenceSelectionCandidateResolverOptions | undefined,
	): SketchEntityReferenceResolvedCandidate | null {
		if (
			!isSketchLineObject(targetObject)
			&& !isSketchCircleObject(targetObject)
			&& !isSketchArcObject(targetObject)
			&& !isSketchEllipseObject(targetObject)
			&& !isSketchParabolaObject(targetObject)
			&& !isSketchSplineObject(targetObject)
			&& !isSketchStyleSplineObject(targetObject)
			&& !isSketchCollectionObject(targetObject)
			&& !isSketchSlotObject(targetObject)
		) {
			return null;
		}

		const sourceSketchId = findAncestorSketchGroupPersistentId(targetObject);
		if (!sourceSketchId || sourceSketchId === targetSketchId) {
			return null;
		}

		if (isSketchSlotObject(targetObject)) {
			return this.resolveSketchSlotCandidate(targetObject, sourceSketchId);
		}

		if (isSketchCollectionObject(targetObject)) {
			return this.resolveSketchCollectionCandidate(targetObject, sourceSketchId);
		}

		if (options?.selectChain === true) {
			const chainResult = this.chainExpansionService.expand({
				sourceSketchId,
				targetSketchId,
				startObject: targetObject,
			});
			const primarySource = chainResult.sources[0];
			if (primarySource) {
				return {
					candidate: {
						descriptor: primarySource.descriptor,
						displayLabel: '链',
						sourceObject: targetObject,
						relatedSources: chainResult.sources,
						intent: {
							intentKind: 'sketch-chain',
							intentId: `sketch-chain::${sourceSketchId}::${buildSelectionIntentSourceListKey(chainResult.sources)}`,
							displayKind: 'chain',
							displayLabel: '链',
						},
					},
				};
			}
		}

		const descriptor: ExternalGeometrySourceDescriptor = {
			kind: 'sketch-entity',
			sourceSketchId,
			entityId: targetObject.persistentId,
		};
		return {
			candidate: {
				descriptor,
				displayLabel: resolveSourceDisplayLabel(targetObject.name ?? ''),
				sourceObject: targetObject,
			},
		};
	}

	/**
	 * 解析闭合草图集合选择。
	 * 说明：
	 * 1. 草图集合是线、圆弧、抛物线、样条等边链的结构化宿主；
	 * 2. Convert Entities 选择到集合或其轮廓面时，应转换集合内的实际曲线来源，而不是把集合本身作为引用实体；
	 * 3. 这里沿用槽口的 relatedSources 模式，使一次选择可以稳定展开为多条来源曲线。
	 *
	 * @param sourceCollection 来源闭合集合。
	 * @param sourceSketchId 来源草图 id。
	 * @returns 集合选择候选；集合为空时返回 null。
	 */
	private resolveSketchCollectionCandidate(
		sourceCollection: SketchCollectionObject,
		sourceSketchId: string,
	): SketchEntityReferenceResolvedCandidate | null {
		const relatedSources: SketchEntityReferenceSelectionCandidateSource[] = sourceCollection.getLines()
			.map((sourceObject) => ({
				descriptor: {
					kind: 'sketch-entity',
					sourceSketchId,
					entityId: sourceObject.persistentId,
				},
				displayLabel: resolveSourceDisplayLabel(sourceObject.name ?? ''),
				sourceObject,
		}));
		const primarySource = relatedSources[0];
		if (!primarySource) {
			return null;
		}

		return {
			candidate: {
				descriptor: primarySource.descriptor,
				displayLabel: primarySource.displayLabel,
				intent: {
					intentKind: 'sketch-composite',
					intentId: `sketch-composite::${sourceSketchId}::${sourceCollection.persistentId}`,
					displayKind: 'chain',
					displayLabel: resolveSourceDisplayLabel(sourceCollection.name ?? ''),
				},
				relatedSources,
			},
		};
	}

	/**
	 * 解析槽口整体选择。
	 * 说明：
	 * 1. SolidWorks Convert Entities 对连续草图实体按边链转换；
	 * 2. PuppyCAD 的槽口是复合对象，内部边才是实际投影与重建单元；
	 * 3. 因此一次槽口选择会展开成 4 个稳定 slot-edge descriptor。
	 *
	 * @param sourceSlot 槽口来源对象。
	 * @param sourceSketchId 来源草图 id。
	 * @returns 槽口选择候选；没有边时返回 null。
	 */
	private resolveSketchSlotCandidate(
		sourceSlot: SketchSlotObject,
		sourceSketchId: string,
	): SketchEntityReferenceResolvedCandidate | null {
		const relatedSources: SketchEntityReferenceSelectionCandidateSource[] = sourceSlot.getEdges()
			.map((sourceObject, edgeIndex) => ({
				descriptor: {
					kind: 'sketch-entity',
					sourceSketchId,
					entityId: sourceSlot.persistentId,
					subEntity: {
						kind: 'slot-edge',
						edgeIndex,
					},
				},
				displayLabel: resolveSourceDisplayLabel(sourceSlot.name ?? ''),
				sourceObject,
			}));
		const primarySource = relatedSources[0];
		if (!primarySource) {
			return null;
		}

		return {
			candidate: {
				descriptor: primarySource.descriptor,
				displayLabel: primarySource.displayLabel,
				intent: {
					intentKind: 'sketch-composite',
					intentId: `sketch-composite::${sourceSketchId}::${sourceSlot.persistentId}`,
					displayKind: 'chain',
					displayLabel: resolveSourceDisplayLabel(sourceSlot.name ?? ''),
				},
				relatedSources,
			},
		};
	}

	/**
	 * 解析 topology owner。
	 *
	 * @param line 当前边代理。
	 * @returns 可持久化的 topology owner；缺少稳定 owner 时返回 null。
	 */
	private resolveTopologyOwner(line: FeatureLine): TopologySourceOwnerDescriptor | null {
		const owningResultMesh = resolveOwningFeatureResultMesh(line);
		const metadata = owningResultMesh?.getFeatureMetadata() ?? line.getFeatureMetadata();
		const featureId = metadata.featureId;
		if (typeof featureId === 'string' && featureId.length > 0) {
			const modelBodyId = typeof metadata.modelBodyId === 'string'
				? metadata.modelBodyId.trim()
				: '';
			const modelBodyRevisionId = typeof metadata.modelBodyRevisionId === 'string'
				? metadata.modelBodyRevisionId.trim()
				: '';
			return {
				ownerKind: 'feature-result',
				ownerId: featureId,
				resultObjectId: owningResultMesh?.objectId ?? line.objectId ?? undefined,
				bodyBindingId: typeof metadata.bodyBindingId === 'string' ? metadata.bodyBindingId : undefined,
				modelBodyId: modelBodyId || undefined,
				modelBodyRevisionId: modelBodyRevisionId || undefined,
			};
		}

		const ownerKind = metadata.topologyOwnerKind;
		const ownerId = metadata.topologyOwnerId;
		if (
			(ownerKind === 'model-body' || ownerKind === 'imported-model-body')
			&& typeof ownerId === 'string'
			&& ownerId.length > 0
		) {
			return {
				ownerKind,
				ownerId,
				resultObjectId: owningResultMesh?.objectId ?? line.objectId ?? undefined,
				bodyBindingId: typeof metadata.bodyBindingId === 'string' ? metadata.bodyBindingId : undefined,
			};
		}

		return null;
	}

	/**
	 * 解析 topology 边的相邻面匹配提示。
	 *
	 * 说明：
	 * 1. hints 只作为来源边身份漂移后的重定位兜底信号；
	 * 2. 目标草图实体类型仍由后续投影结果决定，不能在这里提前推断；
	 * 3. 同时读取快照与 FeatureLine，是为了兼容边代理脱离场景树后仍保留的运行时上下文。
	 *
	 * @param featureLine 当前 topology 边代理。
	 * @param owningResultMesh 边所属的特征结果体。
	 * @returns 可持久化的相邻面提示；没有有效提示时返回 undefined。
	 */
	private resolveTopologyEdgeMatchHints(
		featureLine: FeatureLine,
		owningResultMesh: FeatureResultMesh | null,
	): TopologyEdgeMatchHints | undefined {
		const edgeSnapshot = typeof featureLine.edgeIndex === 'number'
			? owningResultMesh?.getTopologyEdgeSnapshotByIndex(featureLine.edgeIndex)
			: null;
		const adjacentFaceRefs = [
			...(edgeSnapshot?.owningFaceRefs ?? []),
			...featureLine.adjacentFaceRefs,
		].filter((value, index, list) => (
			typeof value === 'string'
			&& value.length > 0
			&& list.indexOf(value) === index
		));
		const adjacentFaceSubEntityIds = featureLine.adjacentFaceIndices
			.map((faceIndex) => owningResultMesh?.getTopologyFaceSnapshotByIndex(faceIndex)?.subEntityId)
			.filter((value, index, list): value is string => (
				typeof value === 'string'
				&& value.length > 0
				&& list.indexOf(value) === index
			));

		if (adjacentFaceRefs.length <= 0 && adjacentFaceSubEntityIds.length <= 0) {
			return undefined;
		}
		return {
			adjacentFaceRefs: adjacentFaceRefs.length > 0 ? adjacentFaceRefs : undefined,
			adjacentFaceSubEntityIds: adjacentFaceSubEntityIds.length > 0 ? adjacentFaceSubEntityIds : undefined,
		};
	}

	/**
	 * 解析 topology 边来源描述。
	 *
	 * @param featureLine 当前边代理。
	 * @returns topology 边来源；无法稳定定位时返回 null。
	 */
	private resolveTopologyEdgeDescriptor(featureLine: FeatureLine): TopologyEdgeSourceDescriptor | null {
		if (typeof featureLine.edgeIndex !== 'number') {
			return null;
		}

		const owner = this.resolveTopologyOwner(featureLine);
		if (!owner) {
			return null;
		}

		const owningResultMesh = resolveOwningFeatureResultMesh(featureLine);
		const edgeSnapshot = owningResultMesh?.getTopologyEdgeSnapshotByIndex(featureLine.edgeIndex) ?? null;
		const curveKind = resolveFeatureLineCurveKind(featureLine, edgeSnapshot);
		const endpoints = featureLine.getWorldEndpoints();
		const hasSampledPoints = edgeSnapshot?.sampledPoints && edgeSnapshot.sampledPoints.length > 1
			|| (curveKind !== 'line' && hasFeatureLineSampledCurveGeometry(featureLine));
		const resolutionLevel = curveKind === 'line' && endpoints
			? 'exact'
			: hasSampledPoints
				? 'sampled-only'
				: 'unsupported';

		const descriptor: TopologyEdgeSourceDescriptor = {
			kind: 'topology-edge',
			owner,
			locator: {
				edgeIndex: featureLine.edgeIndex,
				semanticName: edgeSnapshot?.semanticName,
				persistentRef: edgeSnapshot?.ref ?? featureLine.persistentRef ?? undefined,
				subEntityId: edgeSnapshot?.subEntityId ?? featureLine.subEntityId ?? undefined,
				curveKind,
				resolutionLevel,
				geomFingerprint: curveKind === 'line' && endpoints
					? {
						kind: 'line',
						startWorld: toWorldCoordinateTuple(endpoints.start),
						endWorld: toWorldCoordinateTuple(endpoints.end),
					}
					: undefined,
				matchHints: this.resolveTopologyEdgeMatchHints(featureLine, owningResultMesh),
			},
		};
		return descriptor;
	}

	/**
	 * 解析 topology 面展开候选。
	 *
	 * @param targetObject 当前命中对象。
	 * @returns 命中面时返回其相邻边来源集合；否则返回 null。
	 */
	private resolveTopologyFaceCandidate(targetObject: Object3D): SketchEntityReferenceResolvedCandidate | null {
		if (!isFeatureFace(targetObject)) {
			return null;
		}

		const owningResultMesh = targetObject.getOwningResultMesh();
		if (!owningResultMesh) {
			return null;
		}

		const relatedSourceEntries: Array<{
			descriptor: TopologyEdgeSourceDescriptor;
			displayLabel: string;
		}> = [];
		for (const edgeIndex of targetObject.adjacentEdgeIndices) {
			const featureLine = owningResultMesh.getFeatureLineByIndex(edgeIndex);
			if (!featureLine) {
				return null;
			}

			const descriptor = this.resolveTopologyEdgeDescriptor(featureLine);
			if (!descriptor) {
				return null;
			}

			relatedSourceEntries.push({
				descriptor,
				displayLabel: `边线${descriptor.locator.edgeIndex + 1}`,
			});
		}
		const relatedSources: SketchEntityReferenceSelectionCandidateSource[] = relatedSourceEntries.map((entry) => ({
			descriptor: entry.descriptor,
			displayLabel: entry.displayLabel,
			sourceObject: owningResultMesh.attachFeatureLineByIndex(entry.descriptor.locator.edgeIndex) ?? undefined,
		}));
		const primarySource = relatedSources[0];
		const primaryDescriptor = relatedSourceEntries[0]?.descriptor;
		const faceSnapshot = owningResultMesh.getTopologyFaceSnapshotByIndex(targetObject.faceIndex);
		if (!primarySource || !primaryDescriptor || !faceSnapshot) {
			return null;
		}

		return {
			candidate: {
				descriptor: primarySource.descriptor,
				displayLabel: `面${targetObject.faceIndex + 1}`,
				// Face 的执行来源仍是边集合，但命令选择反馈必须保留实际命中的面代理。
				visualSourceObject: targetObject,
				intent: {
					intentKind: 'topology-face',
					intentId: `topology-face::${resolveTopologySourceOwnerIdentityKey(primaryDescriptor.owner)}::${targetObject.subEntityId}`,
					displayKind: 'face',
					displayLabel: `面${targetObject.faceIndex + 1}`,
					topologyFaceSource: {
						owner: structuredClone(primaryDescriptor.owner),
						locator: {
							faceIndex: faceSnapshot.index,
							semanticName: faceSnapshot.semanticName,
							persistentRef: faceSnapshot.ref,
							subEntityId: faceSnapshot.subEntityId,
							surfaceKind: faceSnapshot.surfaceKind,
							owningFeatureId: faceSnapshot.owningFeatureId,
						},
					},
				},
				relatedSources,
			},
		};
	}

	/**
	 * 解析特征边候选。
	 * 说明：
	 * 1. FeatureLine / FeatureCurve 只是运行时代理，持久来源必须落到 topology-edge；
	 * 2. 当前无法解析稳定 owner 的模型/导入体边不创建引用来源，避免用 uuid 冒充稳定身份；
	 * 3. 非直线边只记录解析等级，是否能进入 preview/create 由 source resolver 判定。
	 *
	 * @param targetObject 当前命中对象。
	 * @returns 命中时返回候选；否则返回 null。
	 */
	private resolveFeatureEdgeCandidate(
		targetObject: Object3D,
	): SketchEntityReferenceResolvedCandidate | null {
		const faceCandidate = this.resolveTopologyFaceCandidate(targetObject);
		if (faceCandidate) {
			return faceCandidate;
		}

		if (!isFeatureLine(targetObject)) {
			return null;
		}

		const descriptor = this.resolveTopologyEdgeDescriptor(targetObject);
		if (!descriptor) {
			return null;
		}

		return {
			candidate: {
				descriptor,
				displayLabel: `边线${descriptor.locator.edgeIndex + 1}`,
				sourceObject: targetObject,
			},
		};
	}
}
